import React, { createContext, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import vehicleService from '../services/vehicleService';

export const VehicleContext = createContext(null);

const EMPTY_ACTIVE_SESSION = {
  active_today: false,
  vehicle_id: null,
  registration: null,
  can_switch_active_vehicle: true,
  switch_rule: null,
};

const getErrorText = (err) => {
  const code = String(err?.response?.data?.code || '').toLowerCase();
  const detail = String(err?.response?.data?.detail || err?.response?.data?.message || '').toLowerCase();
  return { code, detail };
};

const resolveDriverVehicleError = (err) => {
  const { code, detail } = getErrorText(err);

  if (
    code.includes('no_active_vehicle') ||
    detail.includes('must select an active vehicle for today') ||
    detail.includes('no active vehicle')
  ) {
    return {
      kind: 'no_active_vehicle',
      message: 'Driver must select an active vehicle for today.',
    };
  }

  if (
    code.includes('already_selected') ||
    code.includes('cannot_switch') ||
    detail.includes('already selected for today') ||
    detail.includes('log out and sign in again')
  ) {
    return {
      kind: 'switch_locked',
      message: 'Active vehicle already selected for today. Log out and sign in again to switch vehicle.',
    };
  }

  if (
    code.includes('out_of_scope') ||
    detail.includes('out-of-scope vehicle access is not allowed')
  ) {
    return {
      kind: 'out_of_scope',
      message: 'Out-of-scope vehicle access is not allowed.',
    };
  }

  if (
    code.includes('today_scope') ||
    code.includes('wrong_date_scope') ||
    detail.includes('driver scope is limited to today')
  ) {
    return {
      kind: 'wrong_date_scope',
      message: 'Driver scope is limited to today.',
    };
  }

  if (
    code.includes('not_assigned') ||
    detail.includes('not assigned to this driver')
  ) {
    return {
      kind: 'vehicle_unassigned',
      message: 'Selected vehicle is not assigned to this driver.',
    };
  }

  return null;
};

export function VehicleProvider({ children }) {
  const [vehicles, setVehicles]             = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectionRequired, setSelectionRequired] = useState(false);
  const [activeVehicleSession, setActiveVehicleSession] = useState(EMPTY_ACTIVE_SESSION);
  const [loading, setLoading]               = useState(false);

  const setDriverVehicleForToday = useCallback(async (vehicle, userId) => {
    if (!vehicle || !userId) return;
    try {
      const active = await vehicleService.setDriverActiveVehicle(vehicle.id);
      setSelectedVehicle(vehicle);
      setSelectionRequired(false);
      setActiveVehicleSession({
        active_today: Boolean(active?.active_today ?? true),
        vehicle_id: active?.vehicle_id ?? vehicle.id,
        registration: active?.registration ?? vehicle.registration_number ?? null,
        can_switch_active_vehicle: Boolean(active?.can_switch_active_vehicle ?? false),
        switch_rule: active?.switch_rule || null,
      });
      return active;
    } catch (err) {
      const issue = resolveDriverVehicleError(err);
      if (issue) toast.error(issue.message);
      throw err;
    }
  }, []);

  const clearDriverVehicleForToday = useCallback(() => {
    setSelectedVehicle(null);
    setSelectionRequired(false);
    setActiveVehicleSession(EMPTY_ACTIVE_SESSION);
  }, []);

  const handleDriverScopeError = useCallback((err, { notify = true } = {}) => {
    const issue = resolveDriverVehicleError(err);
    if (!issue) return false;

    if (issue.kind === 'no_active_vehicle') {
      setSelectedVehicle(null);
      setSelectionRequired(true);
      setActiveVehicleSession((prev) => ({ ...prev, active_today: false, vehicle_id: null, registration: null }));
    }

    if (notify) toast.error(issue.message);
    return true;
  }, []);

  const loadVehicles = useCallback(async (role) => {
    setLoading(true);
    try {
      if (role === 'driver') {
        const [driverScopedVehicles, active] = await Promise.all([
          vehicleService.getDriverVehicles().catch(() => []),
          vehicleService.getDriverActiveVehicle().catch(() => null),
        ]);

        const availableVehicles =
          Array.isArray(driverScopedVehicles) && driverScopedVehicles.length > 0
            ? driverScopedVehicles
            : await vehicleService.getAll().catch(() => []);

        setVehicles(availableVehicles);

        const activeVehicleId =
          active?.active_vehicle_id ??
          active?.vehicle_id ??
          active?.vehicle?.id ??
          null;

        const activeToday = Boolean(active?.active_today && activeVehicleId);

        const activeVehicle = activeToday
          ? availableVehicles.find((v) => String(v.id) === String(activeVehicleId))
          : null;

        setSelectedVehicle(activeVehicle || null);
        setSelectionRequired(Boolean(availableVehicles.length > 0 && !activeToday));
        setActiveVehicleSession({
          active_today: activeToday,
          vehicle_id: activeVehicleId,
          registration: active?.registration || activeVehicle?.registration_number || null,
          can_switch_active_vehicle: Boolean(active?.can_switch_active_vehicle ?? true),
          switch_rule: active?.switch_rule || null,
        });
      } else {
        const data = await vehicleService.getAll();
        setVehicles(data);
        setSelectionRequired(false);
        setActiveVehicleSession(EMPTY_ACTIVE_SESSION);
        setSelectedVehicle((current) => current || data[0] || null);
      }
    } catch {
      setVehicles([]);
      setSelectedVehicle(null);
      setSelectionRequired(false);
      setActiveVehicleSession(EMPTY_ACTIVE_SESSION);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <VehicleContext.Provider
      value={{
        vehicles,
        selectedVehicle,
        setSelectedVehicle,
        setDriverVehicleForToday,
        clearDriverVehicleForToday,
        handleDriverScopeError,
        activeVehicleSession,
        selectionRequired,
        loadVehicles,
        loading,
      }}
    >
      {children}
    </VehicleContext.Provider>
  );
}